import React, { useState, useEffect, useCallback } from "react";
import "../styles/schedule.css";

const ScheduleList = ({ schedule, phoneView }) => {
  const [eventsData, setEventsData] = useState([]);
  const [collapsedDays, setCollapsedDays] = useState({});
  const [locationFilter, setLocationFilter] = useState("All");
  const [locations, setLocations] = useState(["All"]);

  // Gather all unique locations for the filter
  useEffect(() => {
    const locationSet = new Set(["All"]);
    schedule.forEach((event) => locationSet.add(event.location));
    setLocations([...locationSet]);
  }, [schedule]);

  // Group (and filter) events by day
  const groupEventsByDay = useCallback(
    (events) => {
      const filtered = events.filter(
        (e) => locationFilter === "All" || e.location === locationFilter
      );

      const eventsByDay = filtered.reduce((acc, event) => {
        const eventDate = new Date(
          event.startDate + "T" + event.startTime + ":00"
        );
        const dayKey = eventDate.toISOString().split("T")[0];
        const dayDisplay = eventDate.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
        });

        if (!acc[dayKey]) {
          acc[dayKey] = { events: [], dayDisplay };
        }
        acc[dayKey].events.push(event);
        return acc;
      }, {});

      return Object.entries(eventsByDay)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, { dayDisplay, events }]) => ({
          title: dayDisplay,
          dayKey: key,
          data: events,
        }));
    },
    [locationFilter]
  );

  // Re-group whenever `schedule` or filter changes
  useEffect(() => {
    setEventsData(groupEventsByDay(schedule));
  }, [schedule, groupEventsByDay]);

  // Toggle day collapse
  const toggleDayCollapse = (dayKey) => {
    setCollapsedDays((prev) => ({
      ...prev,
      [dayKey]: !prev[dayKey],
    }));
  };

  // Conditionally add a class for phone layout
  return (
    <div className={`schedule-container ${phoneView ? "phone-dimensions" : ""}`}>
      <h3>Event Schedule</h3>

      {locations.length > 1 && (
        <div className="filter-container">
          {locations.map((loc, idx) => (
            <button
              key={`${loc}-${idx}`}
              className={`filter-button ${
                locationFilter === loc ? "active" : ""
              }`}
              onClick={() => setLocationFilter(loc)}
            >
              {loc}
            </button>
          ))}
        </div>
      )}

      <ul className="event-list">
        {eventsData.map((section) => (
          <li key={section.dayKey} className="event-section">
            <h2 className="section-header" onClick={() => toggleDayCollapse(section.dayKey)}>
              {section.title}
              <span className={`arrow ${collapsedDays[section.dayKey] ? "collapsed" : ""}`}>▼</span>
            </h2>

            {!collapsedDays[section.dayKey] && (
              <ul className="section-events">
                {section.data.map((item) => (
                  <li key={item.id} className="event-item">
                    <div className="event-details">
                      <div style={{ fontWeight: "bold", marginBottom: 5 }}>
                        {item.eventName}
                      </div>
                      <div>{item.location}</div>
                      <div>{item.description}</div>
                    </div>
                    <div className="time-container">
                      {`${item.startTime} - ${item.endTime}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ScheduleList;
